import { formatMoney, formatWeekRange, toDateTime, } from '@tymia/shared';
import { DateTime, } from 'luxon';
import { mapToObj, } from 'remeda';
import { therapeuticFocusType, treatmentMethodType, } from '../user/UserApi.js';
export const enUS = {
    name: 'English',
    language: 'en',
    locale: 'en-US',
    region: 'US',
    dir: 'ltr',
    collator: new Intl.Collator('en-US'),
    urls: [],
    common: {
        paging: {
            prev: 'previous page',
            next: 'next page',
            noSearchResults: 'Unfortunately, we could not find any results matching your search.',
        },
        button: {
            search: 'Search',
            next: 'Next',
        },
        error: {
            network: 'A network error occurred. Please try again later.',
            isRequired: 'This field is required.',
        },
        term: {
            Yes: 'Yes',
            No: 'No',
        },
        validation(error, _context) {
            if (error.keyword === 'type') {
                if (error.params.type === 'string') {
                    return 'This field is required.';
                }
            }
            if (error.keyword === 'enum') {
                if (error.params.allowedValues) {
                    // the value is not one of the allowed values,
                    // but the only case is if the value is missing
                    return 'This field is required.';
                }
            }
            if (error.keyword === 'format') {
                if (error.params.format === 'idn-email') {
                    return 'Please enter a valid email address.';
                }
            }
            if (error.keyword === 'pattern') {
                if (error.params.pattern === '^[0-9]+$') {
                    return 'Only numbers may be entered.';
                }
            }
            return error.message ?? 'Invalid value';
        },
        date(date) {
            const dateTime = toDateTime(date);
            return dateTime.toFormat('LL/dd/y');
        },
        /** enforce time in a particular timezone */
        dateTz(date, zone = 'America/New_York') {
            const dateTime = toDateTime(date);
            return enUS.common.date(dateTime.setZone(zone));
        },
        dateTime(date) {
            const dateTime = toDateTime(date);
            return dateTime.toFormat('LL/dd/y h:mm a');
        },
        time(date) {
            const dateTime = toDateTime(date).setLocale(enUS.locale);
            return dateTime.toLocaleString(DateTime.TIME_SIMPLE);
        },
        formatWeekRange(start, end) {
            return formatWeekRange(start, end, enUS.locale);
        },
        money(value) {
            return formatMoney(value, 'en-US');
        },
    },
    user: {
        PatientNumber: 'Patient number',
        Salutation: 'Salutation',
        Title: 'Title',
        FirstName: 'First name',
        LastName: 'Last name',
        EMail: 'Email address',
        Password: 'Password',
        PhoneCountry: 'Country code',
        PhoneMobile: 'Mobile phone number',
        Website: 'Website',
        InsuranceType: 'Insurance type',
        insuranceTypeLabel(type) {
            return {
                legally: 'Statutory health insurance',
                privately: 'Private health insurance / self-pay',
            }[type];
        },
        Birthday: 'Date of birth',
        CareOf: 'c/o',
        Street: 'Street address',
        PostalCode: 'ZIP code',
        City: 'City',
        Country: 'Country (mailing address)',
        InvoicePostalOnly: 'Invoices requested by mail only',
        InvoiceAddress: 'Different billing address',
        InternalNote: 'Notes',
        InvoiceRemark: 'Note on the invoice',
        name(user) {
            return [user.title, user.firstName, user.lastName].filter(Boolean).join(' ');
        },
        salutationFullName(user) {
            const salutation = {
                Herr: 'Mr.',
                Frau: 'Ms.',
            }[user.salutation] ?? null;
            return [
                {
                    'Mr.': 'Dear Mr.',
                    'Ms.': 'Dear Ms.',
                }[salutation] ?? 'Hello',
                user.title,
                ...salutation ? [] : [user.firstName],
                user.lastName,
            ]
                .filter(Boolean)
                .join(' ');
        },
        JobTitle: 'Professional group',
        jobTitles: {
            Frau: {
                none: '',
                psychologicalPsychotherapist: 'Psychological psychotherapist',
                psychologicalPsychotherapistChildren: 'Child and Adolescent Psychological Psychotherapist',
                specialistPsychiatryPsychotherapy: 'Psychiatrist and Psychotherapist',
                specialistPsychiatryPsychotherapyChildren: 'Child and Adolescent Psychiatrist and Psychotherapist',
                specialistPsychosomatics: 'Physician specialized in Psychosomatic Medicine and Psychotherapy',
                physicianAdditionalQualificationPsychotherapy: 'Physician with additional qualification in psychotherapy',
                nonMedicalPractitionerPsychotherapy: 'Non-medical practitioner for psychotherapy',
                socialPedagogue: 'Social educator',
                coachOrConsultant: 'Coach / consultant',
            },
            Other: {
                none: '',
                psychologicalPsychotherapist: 'Psychological psychotherapist',
                psychologicalPsychotherapistChildren: 'Child and Adolescent Psychological Psychotherapist',
                specialistPsychiatryPsychotherapy: 'Psychiatrist and Psychotherapist',
                specialistPsychiatryPsychotherapyChildren: 'Child and Adolescent Psychiatrist and Psychotherapist',
                specialistPsychosomatics: 'Physician specialized in Psychosomatic Medicine and Psychotherapy',
                physicianAdditionalQualificationPsychotherapy: 'Physician with additional qualification in psychotherapy',
                nonMedicalPractitionerPsychotherapy: 'Non-medical practitioner for psychotherapy',
                socialPedagogue: 'Social educator',
                coachOrConsultant: 'Coach / consultant',
            },
        },
        jobTitle(data) {
            return data.jobTitleFreetext
                ?? this.jobTitles[data.salutation === 'Frau' ? 'Frau' : 'Other'][data.jobTitleType ?? 'none'];
        },
        TherapeuticFocus: 'Therapeutic focus areas',
        TherapeuticFocusShort: 'Focus areas',
        therapeuticFocusNames() {
            return mapToObj(therapeuticFocusType, focus => [focus, [focus]]);
        },
        therapeuticFocusLabel(focus) {
            const value = enUS.user.therapeuticFocusNames()[focus];
            return value?.[0] ?? focus;
        },
        TreatmentMethods: 'Treatment methods',
        treatmentMethodsNames() {
            return mapToObj(treatmentMethodType, treatment => [treatment, [treatment]]);
        },
        treatmentMethodLabel(method) {
            const value = enUS.user.treatmentMethodsNames()[method];
            return value?.[0] ?? method;
        },
        CoverageType: 'Billing',
        coverageTypeLabel(type) {
            return {
                'Private Health Insurance': 'Private health insurance',
                'Statutory Health Insurance': 'Statutory health insurance',
                'Self-Payment': 'Self-pay patients',
                'Reimbursement Procedure': 'Reimbursement procedure',
            }[type];
        },
        Vita: 'Experience, training, qualifications and professional memberships',
        Language: 'Language',
        Languages: 'Languages',
        Profile: 'Profile',
        Gender: 'Gender',
        genderLabel(gender) {
            const labels = { male: 'Male', female: 'Female', diverse: 'Diverse' };
            return labels[gender];
        },
        searchTherapist: {
            metaTitle(params) {
                return `Psychotherapists in ${params.city}: book an appointment online`;
            },
            metaDescription(params) {
                return `In ${params.city}, you can find ${params.count === 0 ? '' : params.count} psychotherapists, psychologists, physicians, and non-medical practitioners for psychotherapy. Easy online appointment booking.`;
            },
            AreYouATherapistOrCoach: 'Are you a therapist or coach?',
            HelpSection: 'Help section',
            YourCity: 'your city',
            psychotherapistsInCity(city) {
                return `Psychotherapists in ${city}`;
            },
            findAndBookPsychotherapyInCity(city) {
                return `Find and book psychotherapy appointments online in ${city}`;
            },
            findAvailableTherapySlots(city) {
                return `At Tymia, you can find available therapy slots and psychological counseling in ${city}. People with statutory health insurance can also visit a private psychotherapy practice in ${city} as self-paying clients or through the statutory health insurance <a href="https://www.tymia.de/psychotherapeuten-finden/kostenerstattungsverfahren-in-der-psychotherapie/">reimbursement procedure</a>. Before booking an appointment, please check which billing options the respective private practice offers. After successfully scheduling your appointment, you will receive a booking confirmation by email.`;
            },
            resultCount(count) {
                const rule = new Intl.PluralRules('en-US').select(count);
                if (rule === 'one') {
                    return `${count} result found`;
                }
                return `${count} results found`;
            },
            distance(km) {
                const mi = km.mul('0.621371');
                return `${mi.round()} mi`;
            },
            ToProfile: 'View profile',
            YourProfile: 'Your profile?\nUpdate details',
            noAvailableAppointments(type) {
                if (type === 'external') {
                    return 'Online appointment booking through Tymia is not available';
                }
                return 'No appointments currently available';
            },
            AvailableRemoteAppointment: 'Available video appointment',
            Advertisement: '-Advertisement-',
            filter: {
                label: 'Filter',
                reset: 'Reset filters',
                showResult: 'Show results',
                address: {
                    placeholder: 'e.g., Munich or 80337',
                },
                location: {
                    label: 'On-site/video',
                    options: {
                        onSite: 'On-site',
                        remote: 'Video',
                    },
                },
                sort: {
                    label: 'Sort by',
                    options: {
                        distance: 'Distance',
                        availability: 'Availability',
                    },
                },
            },
            backLink(city) {
                return `Back to psychotherapists in ${city}`;
            },
        },
        export: {
            PatientRecord: 'Patient record',
            birthdayLabel(birthday) {
                return `DOB: ${birthday}`;
            },
            Diagnoses: 'Diagnoses',
            MainDiagnosis: 'Primary diagnosis',
            File: 'File',
            MedicalHistory: 'Progress notes',
            MedicalHistoryArchive: 'Progress notes archive\n(Edited or deleted entries)',
            Appointments: 'Appointments',
            Invoices: 'Invoice overview',
            InvoiceHeaderLine: 'Invoice number; Invoice date; Name, first name; Invoice amount; Status',
            taxedLabel: 'incl. tax',
        },
    },
    login: {
        tanRequired: 'Please enter the PIN you received by email',
        needVerification: 'Account has not been verified yet. The "Forgot password" function can also be used for this.',
        invalidCredentials: 'Email or password incorrect.',
        invalidTan: 'The entered PIN is invalid.',
        accountLocked: 'Account is locked.',
        submit: 'Login',
    },
    forgotPassword: {
        link: 'Forgot password?',
    },
    client: {
        invoice: {
            download: {
                metaTitle: 'Download invoice',
                title: {
                    error: 'Error',
                    enterTan: 'Confirm identity',
                    download: 'Download invoice',
                },
                message: {
                    invalidToken: 'The download link is invalid or has expired.',
                    notMobile: 'The phone number on file is not a valid mobile number.',
                    enterTan(phone) {
                        return `Please enter the verification code we sent by text message to ${phone}.`;
                    },
                    rateLimited: 'Too many requests. Please try again later.',
                    tooManyTansRequested: 'You have already requested several verification codes. No further verification code is available.',
                    invalidTan: 'The verification code entered is invalid.',
                },
                input: {
                    tan: {
                        label: 'Verification code',
                    },
                    submit: {
                        label: 'Download invoice',
                    },
                },
            },
        },
        medicalHistory: {
            stateLabel(state) {
                return {
                    active: 'Active',
                    changed: 'Changed',
                    deleted: 'Deleted',
                }[state];
            },
        },
    },
    appointment: {
        bookingWidget: {
            SelectInsuranceTypeLabel: 'Please select your insurance type',
            SelectInsuranceTypeSelectEmpty: 'Please select an insurance type first',
            SelectInsuranceTypeSelectSelected: 'Insurance type',
            insuranceTypeOption(type) {
                return enUS.user.insuranceTypeLabel(type);
            },
            meetingTypeOption(meetingType) {
                const location = {
                    onSite: 'on-site',
                    remote: 'online',
                    both: 'on-site and online',
                }[meetingType.location];
                return `${meetingType.name} ${location} (${meetingType.duration} minutes)`;
            },
            SelectMeetingTypeLabel: 'Select appointment type',
            SelectMeetingTypeSelectEmpty: 'Please select an appointment type first',
            SelectMeetingTypeSelectSelected: 'Appointment type',
            SelectAppointment: 'Select appointment',
            NoAppointments: 'No appointments currently available',
            PersonalData: 'Your personal information',
            SelectLocationLabel: 'Select appointment location',
            PrivacyConsent: 'Privacy policy',
            PrivacyConsentText: 'My data will be processed for appointment booking by Tymia GmbH (practice management software) on behalf of the practice.',
            PrivacyConsentLabel: 'I agree to receive appointment confirmations by email.',
            BookAppointment: 'Book appointment',
            BookingSuccessHeader: 'Appointment successfully booked',
            BookingSuccessMessage: 'Please check your email inbox. All additional information will be sent to the email address you provided.',
            bookingSuccessDetails(appointment) {
                return `Appointment starts: ${enUS.common.dateTime(appointment.appointmentStart)}`;
            },
        },
    },
    invoice: {
        stateLabel(stateDescriptor) {
            switch (stateDescriptor.key) {
                case 'CANCELED':
                    return 'Canceled';
                case 'CANCELED_SENT':
                    return 'Canceled (sent)';
                case 'PAID':
                    return 'Paid';
                case 'PAID_SENT':
                    return 'Paid (sent)';
                case 'CREATED':
                    return 'Created';
                case 'SENT':
                    return 'Sent';
                case 'REMINDER_CREATED':
                    return `${stateDescriptor.number}. payment reminder created`;
                case 'REMINDER_SENT':
                    return `${stateDescriptor.number}. payment reminder sent`;
                case 'FINAL_NOTICE_CREATED':
                    return 'Final notice created';
                case 'FINAL_NOTICE_SENT':
                    return 'Final notice sent';
                case 'ERROR':
                    return 'Error';
            }
        },
    },
    footer: {
        AboutTymia: 'About Tymia',
        PrivacyPolicy: 'Privacy Policy',
        Login: 'Login',
        Help: 'Help',
        Imprint: 'Imprint',
    },
    mollie: {
        description(amount) {
            return `${enUS.common.money(amount)} per month, cancel monthly`;
        },
    },
};
//# sourceMappingURL=en-US.js.map